import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";
import { Plus, Trash2, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DateField } from "@/components/DateField";
import api, { fmt, fmtDate, errText, undoToast } from "@/lib/api";

const EXPENSE_CATEGORIES = ["Fabric", "Stitching", "Karigar Payment", "Transport", "Other"];
const EMPTY = { category: "Fabric", date: format(new Date(), "yyyy-MM-dd"), amount: "", remarks: "" };

export default function Expenses() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const { data: expenses, isLoading } = useQuery({ queryKey: ["expenses"], queryFn: async () => (await api.get("/expenses")).data });

  const create = useMutation({
    mutationFn: async () => (await api.post("/expenses", { ...form, amount: Number(form.amount) })).data,
    onSuccess: () => {
      toast.success("Expense added");
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpen(false);
      setForm(EMPTY);
    },
    onError: (e) => toast.error(errText(e)),
  });

  const remove = useMutation({
    mutationFn: async (eid) => (await api.delete(`/expenses/${eid}`)).data,
    onSuccess: (data) => { undoToast("Expense deleted", data.undo_id, () => qc.invalidateQueries({ queryKey: ["expenses"] })); qc.invalidateQueries({ queryKey: ["expenses"] }); },
  });

  const month = format(new Date(), "yyyy-MM");
  const monthTotal = (expenses || []).filter((e) => e.date?.startsWith(month)).reduce((s, e) => s + e.amount, 0);
  const total = (expenses || []).reduce((s, e) => s + e.amount, 0);

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-semibold tracking-tight">Expenses</h1>
          <p className="text-sm text-muted-foreground mt-1">Business expense tracking</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-expense-button" className="rounded-full active:scale-95 transition-transform">
              <Plus className="h-4 w-4 mr-1.5" /> Add Expense
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">New Expense</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Date</Label>
                  <DateField testId="expense-date-field" value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Amount ₹ *</Label>
                <Input data-testid="expense-amount-input" type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Remarks</Label>
                <Input data-testid="expense-remarks-input" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
              </div>
              <Button data-testid="expense-save-button" className="w-full rounded-lg" disabled={!Number(form.amount) || !form.date || create.isPending} onClick={() => create.mutate()}>
                Save Expense
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-xl border bg-card p-5">
          <p className="font-heading text-xl lg:text-2xl font-semibold">{fmt(monthTotal)}</p>
          <p className="text-xs lg:text-sm text-muted-foreground mt-1">This Month</p>
        </div>
        <div className="rounded-xl border bg-card p-5">
          <p className="font-heading text-xl lg:text-2xl font-semibold">{fmt(total)}</p>
          <p className="text-xs lg:text-sm text-muted-foreground mt-1">All Time</p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-14 rounded-xl bg-muted animate-pulse" />)}</div>
      ) : expenses?.length === 0 ? (
        <div className="rounded-xl border bg-card p-12 text-center">
          <Wallet className="h-10 w-10 mx-auto text-muted-foreground/50" />
          <p className="mt-3 font-medium">No expenses recorded</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Date</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Remarks</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenses.map((e) => (
                <TableRow key={e.id} data-testid={`expense-row-${e.id}`} className="hover:bg-muted/50 transition-colors">
                  <TableCell>{fmtDate(e.date)}</TableCell>
                  <TableCell className="font-medium">{e.category}</TableCell>
                  <TableCell className="text-muted-foreground">{e.remarks || "—"}</TableCell>
                  <TableCell className="text-right font-medium">{fmt(e.amount)}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" data-testid={`delete-expense-${e.id}`} onClick={() => remove.mutate(e.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
